import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Plus, Trash2, Save, X, ShoppingBag, Edit3, Package } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, Card, PrimaryButton, SecondaryButton, Input, Textarea, Select, EmptyState, Badge } from "./_ui";

const CATS = ["Vêtements", "Accessoires", "Drapeaux", "Goodies", "Livres"];
const EMPTY = { name: "", description: "", image_url: "", price_usd: 0, stock: 0, category: "Vêtements" };

export default function BoutiquePanel() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || form.price_usd < 0) return toast.error("Nom et prix requis");
    setBusy(true);
    try {
      const payload = { ...form, price_usd: Number(form.price_usd), stock: Number(form.stock) };
      if (editing === "new") await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${editing}`, payload);
      toast.success("Produit enregistré");
      setEditing(null); setForm(EMPTY); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Supprimé"); await load();
  };

  if (!items) return (<><PageHeader title="Boutique" /><Spinner /></>);

  return (
    <div data-testid="admin-boutique">
      <PageHeader
        title="Boutique"
        subtitle="Catalogue d'articles officiels A.F.B. — produits affichés publiquement aux soutiens."
        action={
          editing == null && (
            <PrimaryButton onClick={() => { setEditing("new"); setForm(EMPTY); }} data-testid="prod-create-btn">
              <Plus size={16} /> Nouveau produit
            </PrimaryButton>
          )
        }
      />

      {editing != null && (
        <Card className="p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input placeholder="Nom du produit" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="prod-name" />
            <Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {CATS.map((c) => <option key={c}>{c}</option>)}
            </Select>
            <Input type="number" placeholder="Prix USD" value={form.price_usd} onChange={(e) => setForm({ ...form, price_usd: e.target.value })} data-testid="prod-price" min="0" step="0.01" />
            <Input type="number" placeholder="Stock" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} min="0" />
          </div>
          <Input placeholder="URL image" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="mt-3" />
          <Textarea placeholder="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-3" />
          <div className="flex gap-3 mt-4">
            <PrimaryButton onClick={save} disabled={busy} data-testid="prod-save"><Save size={16} /> Enregistrer</PrimaryButton>
            <SecondaryButton onClick={() => { setEditing(null); setForm(EMPTY); }}><X size={16} /> Annuler</SecondaryButton>
          </div>
        </Card>
      )}

      {items.length === 0 ? (
        <EmptyState icon={ShoppingBag} title="Catalogue vide" hint="Ajoutez le premier article officiel A.F.B." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <Card key={p.id} className="overflow-hidden" data-testid={`prod-${p.id}`}>
              <div className="aspect-square bg-[#0B1220] relative">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-slate-600">
                    <Package size={36} />
                  </div>
                )}
                <Badge color={p.stock > 0 ? "green" : "red"}>
                  {p.stock > 0 ? `Stock : ${p.stock}` : "Rupture"}
                </Badge>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-sm font-medium text-white truncate flex-1">{p.name}</h3>
                  <div className="font-display text-base text-[#D4AF37] shrink-0">${p.price_usd.toFixed(2)}</div>
                </div>
                <Badge color="slate">{p.category}</Badge>
                {p.description && <p className="text-xs text-slate-400 mt-2 line-clamp-2">{p.description}</p>}
                <div className="mt-3 flex gap-2">
                  <button onClick={() => { setEditing(p.id); setForm({ ...p, price_usd: p.price_usd, stock: p.stock }); }} className="flex-1 text-xs text-[#7AB0FF] hover:underline inline-flex items-center justify-center gap-1">
                    <Edit3 size={11} /> Éditer
                  </button>
                  <button onClick={() => remove(p.id)} className="text-xs text-[#FCA5A5] hover:underline inline-flex items-center gap-1">
                    <Trash2 size={11} /> Suppr.
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
